import {
  pgTable,
  uuid,
  text,
  boolean,
  integer,
  jsonb,
  timestamp,
  index,
  check,
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";

const tz = { withTimezone: true } as const;

// ---------------------------------------------------------------------------
// Audience Sync Types
// ---------------------------------------------------------------------------

export const SYNC_TYPES = [
  "retargeting", "customer", "suppression",
  "lookalike_seed", "reengagement", "event_based",
] as const;
export type SyncType = (typeof SYNC_TYPES)[number];

export const SYNC_STATUSES = ["active", "paused", "error", "archived"] as const;
export type SyncStatus = (typeof SYNC_STATUSES)[number];

/** Per-platform audience state (stored as JSONB) */
export interface PlatformAudienceState {
  audienceId?: string;
  matchRate?: number;
  size?: number;
  lastSyncedAt?: string;
  error?: string;
}

// ---------------------------------------------------------------------------
// Audience Syncs — CRM segment → ad platform audience mapping
// ---------------------------------------------------------------------------

export const audienceSyncs = pgTable("audience_syncs", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  name: text("name").notNull(),
  segmentId: uuid("segment_id"),
  syncType: text("sync_type").notNull(),
  platforms: jsonb("platforms").$type<{
    meta?: PlatformAudienceState;
    google?: PlatformAudienceState;
    tiktok?: PlatformAudienceState;
  }>().notNull().default({}),
  autoSync: boolean("auto_sync").notNull().default(false),
  syncIntervalHours: integer("sync_interval_hours").notNull().default(6),
  lastSyncedAt: timestamp("last_synced_at", tz),
  status: text("status").notNull().default("active"),
  memberCount: integer("member_count").notNull().default(0),
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
}, (table) => [
  index("idx_audience_syncs_tenant").on(table.tenantId),
  index("idx_audience_syncs_segment").on(table.segmentId),
  index("idx_audience_syncs_status").on(table.status),
  check("chk_audience_sync_type", sql`${table.syncType} IN ('retargeting', 'customer', 'suppression', 'lookalike_seed', 'reengagement', 'event_based')`),
  check("chk_audience_sync_status", sql`${table.status} IN ('active', 'paused', 'error', 'archived')`),
]);

// ---------------------------------------------------------------------------
// Audience Sync Log — per-execution log (append-only)
// ---------------------------------------------------------------------------

export const audienceSyncLog = pgTable("audience_sync_log", {
  id: uuid("id").primaryKey().defaultRandom(),
  audienceSyncId: uuid("audience_sync_id").notNull(),
  tenantId: uuid("tenant_id").notNull(),
  platform: text("platform").notNull(),
  action: text("action").notNull(), // 'sync', 'add', 'remove'
  contactsAdded: integer("contacts_added").notNull().default(0),
  contactsRemoved: integer("contacts_removed").notNull().default(0),
  matchRate: integer("match_rate"), // percentage 0-100
  error: text("error"),
  syncedAt: timestamp("synced_at", tz).notNull().defaultNow(),
  // APPEND-ONLY: no updatedAt
}, (table) => [
  index("idx_audience_sync_log_sync").on(table.audienceSyncId, table.syncedAt),
  index("idx_audience_sync_log_tenant").on(table.tenantId, table.syncedAt),
]);

// ---------------------------------------------------------------------------
// CAPI Event Log — conversion event delivery tracking
// ---------------------------------------------------------------------------
// Tracks each conversion event pushed to ad platforms.
// event_id is the attribution event ID for dedup.
// ONLY hashed PII is stored here. NEVER raw email/phone.

export const CAPI_DELIVERY_STATUSES = ["pending", "sent", "failed", "deduped"] as const;
export type CapiDeliveryStatus = (typeof CAPI_DELIVERY_STATUSES)[number];

export const capiEventLog = pgTable("capi_event_log", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  eventId: text("event_id").notNull(), // attribution event ID (for dedup)
  platform: text("platform").notNull(),
  eventType: text("event_type").notNull(), // 'purchase', 'lead', 'booking'
  // Hashed PII only — NEVER raw
  hashedEmail: text("hashed_email"),
  hashedPhone: text("hashed_phone"),
  // Delivery state
  status: text("status").notNull().default("pending"),
  retryCount: integer("retry_count").notNull().default(0),
  lastError: text("last_error"),
  // Value
  valueCents: integer("value_cents"),
  currency: text("currency").default("EUR"),
  // Timestamps
  sentAt: timestamp("sent_at", tz),
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
}, (table) => [
  index("idx_capi_event_tenant").on(table.tenantId, table.createdAt),
  index("idx_capi_event_dedup").on(table.tenantId, table.eventId, table.platform),
  index("idx_capi_event_status").on(table.status),
  check("chk_capi_platform", sql`${table.platform} IN ('meta', 'google', 'tiktok')`),
  check("chk_capi_status", sql`${table.status} IN ('pending', 'sent', 'failed', 'deduped')`),
]);
